import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ChevronDown, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { OrderReplacement } from '../types';

const STATUS_OPTIONS = ['Pending', 'Approved', 'In Progress', 'Completed', 'Rejected'];

const STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  'Pending':     { bg: '#fef9c3', color: '#92400e' },
  'Approved':    { bg: '#dbeafe', color: '#1e40af' },
  'In Progress': { bg: '#ffedd5', color: '#9a3412' },
  'Completed':   { bg: '#d1fae5', color: '#065f46' },
  'Rejected':    { bg: '#fee2e2', color: '#991b1b' },
};

const ReplacementsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const technicianId = user?.technicianIds?.[0];

  const [items, setItems]         = useState<OrderReplacement[]>([]);
  const [loading, setLoading]     = useState(false);
  const [statusFilter, setFilter] = useState('');
  const [updating, setUpdating]   = useState<number | null>(null);
  const [openMenu, setOpenMenu]   = useState<number | null>(null);
  const [flashMsg, setFlashMsg]   = useState<{ text: string; ok: boolean } | null>(null);

  const load = useCallback(async () => {
    if (!technicianId) return;
    setLoading(true);
    try {
      const res = await apiService.getMyReplacements(technicianId, statusFilter || undefined);
      setItems(res?.entity ?? []);
    } catch {
      flash('Failed to load replacements', false);
    } finally {
      setLoading(false);
    }
  }, [technicianId, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Close status menu on outside tap
  useEffect(() => {
    if (openMenu === null) return;
    const h = () => setOpenMenu(null);
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [openMenu]);

  const flash = (text: string, ok: boolean) => {
    setFlashMsg({ text, ok });
    setTimeout(() => setFlashMsg(null), 2800);
  };

  const handleStatusChange = async (r: OrderReplacement, newStatus: string) => {
    setOpenMenu(null);
    if (r.replacementStatus === newStatus) return;
    setUpdating(r.id);
    try {
      const res = await apiService.updateReplacementStatus(r, newStatus);
      if (res?.isSuccess !== false) {
        flash(`Status updated to ${newStatus}`, true);
        setItems(prev => prev.map(x => x.id === r.id ? { ...x, replacementStatus: newStatus } : x));
      } else {
        flash('Failed to update status', false);
      }
    } catch {
      flash('Failed to update status', false);
    } finally {
      setUpdating(null);
    }
  };

  const fmt = (d: string) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const fmtCost = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`;

  return (
    <div style={{ padding: '0 0 80px' }}>
      {/* Flash message */}
      {flashMsg && (
        <div style={{
          position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)',
          zIndex: 999, padding: '10px 20px', borderRadius: 10, fontWeight: 600, fontSize: 14,
          background: flashMsg.ok ? '#065f46' : '#991b1b', color: '#fff',
          boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
        }}>
          {flashMsg.text}
        </div>
      )}

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <RefreshCw size={18} color="#fff" />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 17, color: '#172033' }}>Replacements</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>{items.length} record{items.length !== 1 ? 's' : ''}</div>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', fontSize: 13, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : undefined }} />
          Refresh
        </button>
      </div>

      {/* Status filter chips */}
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, marginBottom: 16 }}>
        {['', ...STATUS_OPTIONS].map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            style={{
              flexShrink: 0,
              padding: '5px 14px',
              borderRadius: 20,
              border: 'none',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              background: statusFilter === s ? '#7c3aed' : '#e2e8f0',
              color: statusFilter === s ? '#fff' : '#475569',
            }}
          >
            {s || 'All'}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
          <RefreshCw size={28} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} />
          <div>Loading...</div>
        </div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
          <RefreshCw size={36} style={{ marginBottom: 12, opacity: 0.3 }} />
          <div style={{ fontWeight: 600, marginBottom: 4 }}>No replacements</div>
          <div style={{ fontSize: 13 }}>No replacement records assigned to you.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {items.map(r => {
            const style = STATUS_STYLE[r.replacementStatus] ?? { bg: '#f1f5f9', color: '#475569' };
            const isUpdating = updating === r.id;

            return (
              <div
                key={r.id}
                style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
              >
                {/* Top row: order # + cost */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#172033' }}>
                      {r.orderNumber || `Order #${r.orderId}`}
                    </div>
                    {r.customerName && (
                      <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{r.customerName}</div>
                    )}
                    {r.customerMobile && (
                      <a
                        href={`tel:${r.customerMobile}`}
                        style={{ fontSize: 13, color: '#7c3aed', fontWeight: 600, textDecoration: 'none', display: 'inline-block', marginTop: 2 }}
                      >
                        📞 {r.customerMobile}
                      </a>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 700, fontSize: 16, color: '#172033' }}>{fmtCost(r.replacementCost)}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{fmt(r.replacementDate)}</div>
                  </div>
                </div>

                {/* Notes */}
                {r.notes && (
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 10, padding: '6px 10px', background: '#f8fafc', borderRadius: 8, borderLeft: '3px solid #e2e8f0' }}>
                    {r.notes}
                  </div>
                )}

                {/* Status row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>Status</span>

                  {/* Status selector */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                      onClick={() => navigate(`/orders/${r.orderId}?readonly=1`)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        padding: '5px 12px',
                        borderRadius: 20,
                        border: '1px solid #e2e8f0',
                        background: '#f8fafc',
                        color: '#475569',
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: 'pointer',
                      }}
                    >
                      <ExternalLink size={12} />
                      View Details
                    </button>
                  <div style={{ position: 'relative' }}>
                    <button
                      disabled={isUpdating}
                      onMouseDown={(e) => { e.stopPropagation(); setOpenMenu(openMenu === r.id ? null : r.id); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '5px 12px',
                        borderRadius: 20,
                        border: 'none',
                        background: style.bg,
                        color: style.color,
                        fontWeight: 700,
                        fontSize: 13,
                        cursor: isUpdating ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {isUpdating ? 'Updating…' : r.replacementStatus}
                      {!isUpdating && <ChevronDown size={13} />}
                    </button>

                    {openMenu === r.id && (
                      <div
                        onMouseDown={e => e.stopPropagation()}
                        style={{
                          position: 'absolute', right: 0, bottom: '110%',
                          background: '#fff', border: '1px solid #e2e8f0',
                          borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          zIndex: 50, minWidth: 160, overflow: 'hidden',
                        }}
                      >
                        {STATUS_OPTIONS.map(s => {
                          const st = STATUS_STYLE[s] ?? { bg: '#f1f5f9', color: '#475569' };
                          const isActive = r.replacementStatus === s;
                          return (
                            <button
                              key={s}
                              onClick={() => handleStatusChange(r, s)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                width: '100%', padding: '10px 14px',
                                background: isActive ? '#f8f5ff' : '#fff',
                                border: 'none',
                                textAlign: 'left',
                                cursor: 'pointer',
                                fontSize: 13,
                                color: '#172033',
                                borderBottom: '1px solid #f1f5f9',
                              }}
                            >
                              <span style={{ width: 10, height: 10, borderRadius: '50%', background: st.color, display: 'inline-block', flexShrink: 0 }} />
                              <span style={{ fontWeight: isActive ? 700 : 400 }}>{s}</span>
                              {isActive && <span style={{ marginLeft: 'auto', color: '#7c3aed', fontSize: 12 }}>✓</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default ReplacementsPage;
